import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { orderBy as odb, chunk, escapeRegExp } from "lodash";
import {
  FormControl,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { AlarmOffSharp, ReplaySharp, Search } from "@mui/icons-material";
import { ReactComponent as FilterSvg } from "assets/Svgs/filter.svg";
import { useBoundStore } from "store/store";
import moment from "moment";
import i18n from "i18n/i18n";
import { Button } from "antd";

interface DeviceMessages {
  areaName?: string;
  equipmentName?: string;
  machineData?: any;
}
interface Data {
  id: number;
  Zeitpunkt: string;
  description: string;
  Meldungstyp: string;
  Artikel: string;
  ProduktNummer: string;
  Charge: string;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order | any,
  orderBy: Key | any
) {
  return order === "desc"
    ? (a: any, b: any) => descendingComparator(a, b, orderBy)
    : (a: any, b: any) => -descendingComparator(a, b, orderBy);
}

const getHighlightedText = (text: any, highlight: any) => {
  if (!highlight.trim()) return <span>{text}</span>;
  const regex = new RegExp(`(${escapeRegExp(highlight)})`, "gi");
  const parts = text?.split(regex);
  return (
    <span>
      {parts?.map((part: any, i: number) =>
        regex.test(part) ? (
          <span
            style={{ color: "var(--primary-color)", fontWeight: "bold" }}
            key={i}
          >
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  lang: string;
  activeId?: any;
  setActiveId: any;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const headCells: readonly any[] = [
    {
      id: "Zeitpunkt",
      numeric: false,
      disablePadding: false,
      label: i18n(props.lang)?.time_nkt,
    },
    {
      id: "Meldungstyp",
      numeric: false,
      disablePadding: false,
      label: i18n(props.lang)?.notificationtype + " (No.)",
    },
    {
      id: "Interner",
      numeric: false,
      disablePadding: false,
      label: i18n(props.lang).devicetype,
    },
    {
      id: "Bereich",
      numeric: false,
      disablePadding: true,
      label: i18n(props.lang).single_area,
    },
    {
      id: "Gerätetyp",
      numeric: true,
      disablePadding: false,
      label: i18n(props.lang).device_type,
    },
  ];

  const { order, orderBy, onRequestSort, setActiveId } = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
      setActiveId(property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            className="tableHead"
            width={"25%"}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
              sx={{ display: "flex", flexDirection: "row" }}
              IconComponent={FilterSvg}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const DeviceMessages: React.FC<DeviceMessages> = ({ machineData }) => {
  const lang: any = useBoundStore((state) => state.lang);
  const getAreaEqsEvents = useBoundStore((state) => state.getAreaEqsEvents);
  let areaEqsEvents = useBoundStore((state) => state.areaEqsEvents);

  // filter only current equipment events
  if (areaEqsEvents.length) {
    areaEqsEvents = areaEqsEvents.filter(
      (event: any) => event.equipmentNodeId === machineData.equipment.nodeid
    );
  }

  // table states
  const [order, setOrder] = React.useState<Order>("desc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("id");
  const [selected, setSelected] = React.useState<readonly number[]>([]);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [activeId, setActiveId] = React.useState("Zeitpunkt");
  const [searchText, setSearchText] = React.useState("");
  const [searchData, setSearchData] = React.useState<Data[] | []>([]);

  // date filter states
  const getYesterdayStartTimestamp = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  };
  const now = new Date();
  now.setHours(23, 59, 59, 999);

  const [startDate, setStartDate] = React.useState(
    moment(getYesterdayStartTimestamp()).format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = React.useState(
    moment(now.getTime()).format("YYYY-MM-DD")
  );

  const handleStartTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };
  const handleEndTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  const handleLoad = () => {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    getAreaEqsEvents(
      [machineData.equipment.nodeid],
      start.getTime(),
      end.getTime()
    );
  };

  const handleClear = () => {
    const start = getYesterdayStartTimestamp();
    const end = now.getTime();
    setStartDate(moment(start).format("YYYY-MM-DD"));
    setEndDate(moment(end).format("YYYY-MM-DD"));
    getAreaEqsEvents([machineData.equipment.nodeid], start, end);
  };
  

  // sorting
  function stableSort<T>(array: readonly T[]) {
    return odb(array, ["date"], [order]);
  }

  const handleRequestSort = (
    e: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelected(areaEqsEvents.map((n: any) => n.id));
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (e: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (e: any) => {
    setRowsPerPage(+e.target.value);
    setPage(1);
    (window as any).blurAll();
  };

  // search
  const handleSearch = (e: any) => {
    const text = e.target.value;
    setSearchText(text);
    if (text.length) {
      setPage(1);
      setSearchData(
        areaEqsEvents.filter((row: any) =>
          [
            row.date,
            row.description,
            row.meldungType,
            row.interner,
            row.bereich,
            row.productionaline,
          ].some((field) => field?.toLowerCase().includes(text.toLowerCase()))
        )
      );
    } else {
      setSearchData(areaEqsEvents);
    }
  };

  React.useEffect(() => { 
    handleLoad();
  }, []);

 

  const searchEnabled = searchText.length;
  const paginationchunks = chunk(
    searchEnabled ? searchData : areaEqsEvents,
    rowsPerPage
  );
  const emptyRows =
    page > 1
      ? Math.max(
          0,
          page * rowsPerPage -
            (searchEnabled ? searchData.length : areaEqsEvents.length)
        )
      : 0;

  return (
    <>
      <div style={{ display: "flex",alignItems: "start", justifyContent: "space-between"}}>
        <div className="generalHeading" style={{ fontSize: "20px" }}>
          {i18n(lang)?.tab_device_messages}
        </div>
        {/* filters */}
        <div className="dashboardTableHeader dashb" style={{ paddingTop: 0}}>
          <div className="cal1">
            <TextField
              type="date"
              label={i18n(lang)?.start_time}
              variant="standard"
              value={startDate}
              onChange={handleStartTime}
            />
          </div>
          <div className="cal2">
            <TextField
              type="date"
              label={i18n(lang)?.end_time}
              variant="standard"
              value={endDate}
              onChange={handleEndTime}
            />
          </div>
          <div style={{ marginRight: 10 }}>
            <Button onClick={handleLoad}>
              <Tooltip title={i18n(lang)?.load_event_in_time_frame}>
                <ReplaySharp />
              </Tooltip>
            </Button>
          </div>
          <div style={{ marginRight: 10 }}>
            <Button onClick={handleClear}>
              <Tooltip title={i18n(lang)?.reset_time_frame}>
                <AlarmOffSharp />
              </Tooltip>
            </Button>
          </div>
          <div>
            <TextField
              className="tableSearchField"
              label={i18n(lang)?.enter_search_term}
              value={searchText}
              onChange={handleSearch}
              variant="standard"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </div>
        </div>
      </div>

      {/* table */}
      <Table className="dashboardTable">
        <EnhancedTableHead
          numSelected={selected.length}
          order={order}
          orderBy={orderBy}
          onSelectAllClick={handleSelectAllClick}
          onRequestSort={handleRequestSort}
          rowCount={searchEnabled ? searchData.length : areaEqsEvents.length}
          lang={lang}
          setActiveId={setActiveId}
          activeId={activeId}
        />
        <TableBody>
          {(searchEnabled ? searchData : areaEqsEvents).length > 0 ? (
            stableSort(searchEnabled ? searchData : areaEqsEvents)
              .slice(
                (page - 1) * rowsPerPage,
                (page - 1) * rowsPerPage + rowsPerPage
              )
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;
                return(
                <TableRow
                key={index}
                style={{
                  height:
                    document.getElementById("rowDes-" + (index + 1))
                      ?.offsetHeight + "px",
                }}
                className="mainRow"
             
              >
                <TableCell
                  component="th"
                  scope="row"
                  style={{
                    color: "#000000DE",
                    width: "30%",
                    position: "relative",
                    paddingTop: "10px",
                    paddingBottom:
                      row.description.length >= 150 ? "43px" : "27px",
                  }}
                  id={labelId}
                  className="mainTH"
                >
                  <div className="rowTitle">
                    {getHighlightedText(row.date, searchText)}
                  </div>
                  <div className="rowDesc" id={"rowDes-" + index}>
                    <Tooltip
                      title={"" + row.description}
                      placement="left-start"
                    >
                      {getHighlightedText(row.description, searchText)}
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell
                  align="right"
                  className={
                    typeof row.status === "string" &&
                    row.status?.toLowerCase().includes("error")
                      ? "errorText"
                      : ""
                  }
                >
                  <div className="rowTitle">
                    {getHighlightedText(row.meldungType, searchText)}
                  </div>
                </TableCell>

                <TableCell align="right">
                  <div className="rowTitle">
                    {getHighlightedText(row.interner, searchText)}
                  </div>
                </TableCell>
                <TableCell align="right">
                  <div className="rowTitle">
                    {getHighlightedText(row.bereich, searchText)}
                  </div>
                </TableCell>
                <TableCell
                  align="right"
                  className={
                    index ===
                    (searchEnabled ? searchData : areaEqsEvents).length - 1
                      ? "lastTableRowCell"
                      : ""
                  }
                >
                  <div className="rowTitle">
                    {getHighlightedText(row.productionaline, searchText)}
                  </div>
                </TableCell>
              </TableRow>)})
              
          ) : (
            <TableRow>
              <TableCell colSpan={5} align="center">
                {i18n(lang)?.no_data}
              </TableCell>
            </TableRow>
          )}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={5} />
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* pagination */}
      <div className="DashboardTablePagination">
        <div>
          {paginationchunks.length > 1 && (
            <Pagination
              count={paginationchunks.length}
              shape="rounded"
              onChange={handleChangePage}
              page={page}
              hideNextButton={page === paginationchunks.length}
              hidePrevButton={page === 1}
            />
          )}
        </div>
        <div>
          <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              className="perPageSelect"
            >
              <MenuItem value={5}>
                {5}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={10}>
                {10}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={25}>
                {25}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
              <MenuItem value={50}>
                {50}
                {i18n(lang)?.rows_per_page}
              </MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
    </>
  );
};

export default DeviceMessages;
